import React from "react";
import { FiFileText, FiMessageCircle, FiMic, FiUsers, FiCheck, FiArrowRight } from "react-icons/fi";

const ScoringMethods = () => {
  const methods = [
    {
      id: "matching_resume",
      name: "Matching Resume",
      icon: FiFileText,
      color: "purple",
      description: "AI analyzes the candidate's resume against job requirements",
      howItWorks: [
        "Candidate uploads their CV/Resume",
        "AI extracts skills, experience, education, and languages",
        "System compares against job qualification spec",
        "Score calculated based on weighted criteria match",
      ],
      pros: ["Fast automated screening", "Consistent evaluation", "Handles high volume"],
      cons: ["Limited to resume content", "Cannot verify soft skills", "May miss potential"],
      bestFor: "Initial screening of large applicant pools",
    },
    {
      id: "custom_questions",
      name: "Custom Questions",
      icon: FiMessageCircle,
      color: "blue",
      description: "Candidates answer specific qualifying questions",
      howItWorks: [
        "Recruiter defines custom questions for the role",
        "Candidates answer during application",
        "AI scores responses against expected answers",
        "Knockout questions can auto-disqualify",
      ],
      pros: ["Verify specific knowledge", "Filter by availability/requirements", "Quick to complete"],
      cons: ["Candidates can prepare answers", "Text-based only", "Limited depth"],
      bestFor: "Verifying hard requirements (visa, availability, specific skills)",
    },
    {
      id: "ia_interviewer",
      name: "IA Interviewer",
      icon: FiMic,
      color: "green",
      description: "AI conducts conversational interviews via voice or chat",
      howItWorks: [
        "AI asks dynamic follow-up questions",
        "Candidate responds via voice or text",
        "AI evaluates communication skills and depth of answers",
        "Generates interview transcript and score breakdown",
      ],
      pros: ["Tests communication skills", "Deeper assessment", "Consistent interview experience"],
      cons: ["Longer candidate time commitment", "Requires good audio setup", "Some candidates uncomfortable"],
      bestFor: "Roles requiring strong communication (sales, customer-facing)",
    },
    {
      id: "personality_test",
      name: "Personality Test",
      icon: FiUsers,
      color: "orange",
      description: "Assess personality traits and cultural fit",
      howItWorks: [
        "Candidate completes standardized assessment (MBTI, Big Five, etc.)",
        "System generates personality profile",
        "Compare against ideal candidate profile for role",
        "Identify potential team dynamics and fit",
      ],
      pros: ["Assess soft skills objectively", "Predict team fit", "Identify strengths/blind spots"],
      cons: ["Can be gamed", "Not predictive of job performance alone", "Time consuming"],
      bestFor: "Team fit assessment, leadership roles, culture-critical positions",
    },
  ];

  const colorClasses = {
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      icon: "bg-purple-100 text-purple-600",
      badge: "bg-purple-100 text-purple-700",
    },
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      icon: "bg-blue-100 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      icon: "bg-green-100 text-green-600",
      badge: "bg-green-100 text-green-700",
    },
    orange: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      icon: "bg-orange-100 text-orange-600",
      badge: "bg-orange-100 text-orange-700",
    },
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Scoring Methods</h2>
        <p className="text-gray-600">Different ways to evaluate and score candidates during the qualification process.</p>
      </div>

      <div className="space-y-6">
        {methods.map((method) => {
          const Icon = method.icon;
          const colors = colorClasses[method.color];

          return (
            <div key={method.id} className={`bg-white rounded-lg border ${colors.border} overflow-hidden`}>
              {/* Header */}
              <div className={`${colors.bg} px-6 py-4 border-b ${colors.border}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-lg ${colors.icon}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{method.name}</h3>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* How it works */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">How it works</h4>
                    <div className="space-y-2">
                      {method.howItWorks.map((step, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-gray-700">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pros & Cons */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Pros</h4>
                      <div className="space-y-1">
                        {method.pros.map((pro, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-green-700">
                            <FiCheck className="w-4 h-4 text-green-500" />
                            <span>{pro}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Cons</h4>
                      <div className="space-y-1">
                        {method.cons.map((con, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-red-700">
                            <span className="w-4 h-4 text-red-500 text-center">−</span>
                            <span>{con}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Best for */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.badge}`}>Best for</span>
                    <span className="text-sm text-gray-700">{method.bestFor}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommended Flow */}
      <div className="mt-8 bg-gray-900 rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4">Recommended Qualification Flow</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-purple-500/20 text-purple-300 px-3 py-2 rounded-lg">
            <FiFileText className="w-4 h-4" />
            <span className="text-sm font-medium">Resume Match</span>
          </div>
          <FiArrowRight className="w-4 h-4 text-gray-500" />
          <div className="flex items-center gap-2 bg-blue-500/20 text-blue-300 px-3 py-2 rounded-lg">
            <FiMessageCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Custom Questions</span>
          </div>
          <FiArrowRight className="w-4 h-4 text-gray-500" />
          <div className="flex items-center gap-2 bg-green-500/20 text-green-300 px-3 py-2 rounded-lg">
            <FiMic className="w-4 h-4" />
            <span className="text-sm font-medium">AI Interview</span>
          </div>
          <FiArrowRight className="w-4 h-4 text-gray-500" />
          <div className="flex items-center gap-2 bg-orange-500/20 text-orange-300 px-3 py-2 rounded-lg">
            <FiUsers className="w-4 h-4" />
            <span className="text-sm font-medium">Personality</span>
          </div>
        </div>
        <p className="text-gray-400 text-sm mt-4">
          Start with fast automated screening (resume), then verify requirements (questions), assess communication (AI interview), and finally evaluate fit (personality).
        </p>
      </div>
    </div>
  );
};

export default ScoringMethods;
